import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { FiDownload, FiLink, FiSend } from 'react-icons/fi';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { theme } from '../../theme';

/** Inline Android mark (avoids react-icons/fa export differences across versions). */
function AndroidIconGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        fill="currentColor"
        d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24a11.14 11.14 0 00-8.94 0L5.65 5.67a.66.66 0 00-.87-.2c-.28.18-.37.54-.22.83L6.4 9.48A10.23 10.23 0 001.28 18h21.44c0-3.56-1.98-6.75-5.12-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25S6.31 12.75 7 12.75 8.25 13.31 8.25 14 7.69 15.25 7 15.25zm10 0c-.69 0-1.25-.56-1.25-1.25S16.31 12.75 17 12.75 18.25 13.31 18.25 14 17.69 15.25 17 15.25z"
      />
    </svg>
  );
}

/** Base / Drive APK from env (stable link). */
const BASE_DOWNLOAD_URL = (
  process.env.REACT_APP_ANDROID_DRIVE_URL ||
  process.env.REACT_APP_ANDROID_DOWNLOAD_URL ||
  ''
).trim();
const SHARE_URL = (
  process.env.REACT_APP_ANDROID_SHARE_URL ||
  process.env.REACT_APP_ANDROID_DRIVE_URL ||
  process.env.REACT_APP_ANDROID_DOWNLOAD_URL ||
  ''
).trim();

const VERSION_JSON_URL = (
  process.env.REACT_APP_ANDROID_VERSION_JSON_URL || 'https://guards.dynamixzone.com/app/version.json'
).trim();

/** Reserved for future iOS / Windows rows. */
const IOS_DOWNLOAD_URL = (process.env.REACT_APP_IOS_DOWNLOAD_URL || '').trim();
const WINDOWS_DOWNLOAD_URL = (process.env.REACT_APP_WINDOWS_DOWNLOAD_URL || '').trim();

interface AppVersionManifest {
  version: string;
  buildNumber: string;
  downloadUrl: string;
  releaseNotes: string;
  forceUpdate: boolean;
  minVersion: string;
}

function isManifest(x: unknown): x is AppVersionManifest {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.version === 'string' &&
    typeof o.buildNumber === 'string' &&
    typeof o.downloadUrl === 'string' &&
    typeof o.releaseNotes === 'string' &&
    typeof o.forceUpdate === 'boolean' &&
    typeof o.minVersion === 'string'
  );
}

/** Show full URL in modal; long strings wrap instead of clipping. */
function displayUrl(url: string, maxLen = 200) {
  const u = url.trim();
  if (u.length <= maxLen) return u;
  return `${u.slice(0, Math.floor(maxLen / 2) - 1)}…${u.slice(-Math.floor(maxLen / 2) + 1)}`;
}

const DownloadStrip = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.dark.border.light};
  background: ${theme.colors.dark.background.primary};
`;

const DownloadLabel = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.dark.text.secondary};
  font-weight: ${theme.typography.fontWeight.medium};
  user-select: none;
`;

const PlatformIcons = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
`;

const PlatformIconButton = styled.button<{ $accent?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: ${theme.borderRadius.md};
  background: transparent;
  color: ${({ $accent }) => $accent || theme.colors.dark.text.secondary};
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease;

  &:hover {
    background-color: ${theme.colors.dark.background.secondary};
    color: ${({ $accent }) => $accent || theme.colors.white};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary[500]};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }
`;

const ModalActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const VersionMeta = styled.p`
  margin: 0 0 ${theme.spacing[2]} 0;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.light.text.primary};
`;

const ReleaseNotesBlock = styled.div`
  margin: 0 0 ${theme.spacing[4]} 0;
  max-height: 100px;
  overflow-y: auto;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.secondary[100]};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
  line-height: 1.45;
  white-space: pre-wrap;
`;

const ModalHint = styled.p`
  margin: 0 0 ${theme.spacing[3]} 0;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  line-height: 1.5;
`;

const ModalWarning = styled.p`
  margin: 0 0 ${theme.spacing[3]} 0;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.accent.danger};
  line-height: 1.5;
`;

const DownloadStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  width: 100%;
`;

const BaseDriveSection = styled.div`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.secondary[50]};
  border: 1px solid ${theme.colors.light.border.light};
`;

const BaseDriveTitle = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.light.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: ${theme.spacing[2]};
`;

const BaseDriveLink = styled.a`
  display: block;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.primary[600]};
  text-decoration: underline;
  text-underline-offset: 3px;
  word-break: break-all;
  line-height: 1.45;

  &:hover {
    color: ${theme.colors.primary[700]};
  }
`;

const BaseDriveMissing = styled.p`
  margin: 0;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.muted};
  line-height: 1.45;
`;

const LatestUrlHint = styled.p`
  margin: ${theme.spacing[1]} 0 0 0;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.muted};
  word-break: break-all;
  line-height: 1.4;
`;

const CopyShareRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: ${theme.spacing[2]};
  width: 100%;
`;

const CopyShareRowButton = styled.div`
  flex: 1;
  min-width: 0;

  button {
    width: 100%;
  }
`;

interface SidebarAndroidDownloadProps {
  onClose?: () => void;
}

/**
 * Fetches version.json for latest APK URL; env provides base Drive/APK link.
 */
const SidebarAndroidDownload: React.FC<SidebarAndroidDownloadProps> = ({ onClose }) => {
  const [androidModalOpen, setAndroidModalOpen] = useState(false);
  const [manifest, setManifest] = useState<AppVersionManifest | null>(null);
  const [versionLoading, setVersionLoading] = useState(false);
  const [versionError, setVersionError] = useState<string | null>(null);

  const hasBaseDownloadUrl = BASE_DOWNLOAD_URL.length > 0;

  const latestDownloadUrl = (manifest?.downloadUrl ?? '').trim();
  const hasLatestDownload = latestDownloadUrl.length > 0;

  const primaryShareUrl = useMemo(() => {
    if (latestDownloadUrl) return latestDownloadUrl;
    return SHARE_URL;
  }, [latestDownloadUrl]);

  const hasShareTarget = primaryShareUrl.length > 0;

  useEffect(() => {
    if (!androidModalOpen) return;

    const ac = new AbortController();
    setManifest(null);
    setVersionError(null);
    setVersionLoading(true);

    fetch(VERSION_JSON_URL, { signal: ac.signal, cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error(`Could not load version (${res.status})`);
        return res.json() as unknown;
      })
      .then((data) => {
        if (!isManifest(data)) throw new Error('Invalid version.json format');
        setManifest(data);
      })
      .catch((e: Error) => {
        if (e.name === 'AbortError') return;
        setVersionError(e.message || 'Could not load version.json');
        setManifest(null);
      })
      .finally(() => setVersionLoading(false));

    return () => ac.abort();
  }, [androidModalOpen]);

  const closeDrawer = () => {
    onClose?.();
  };

  const handleOpenLatestApk = () => {
    if (!hasLatestDownload) {
      toast.error('Latest APK URL is not available yet. Try again after version.json loads.');
      return;
    }
    window.open(latestDownloadUrl, '_blank', 'noopener,noreferrer');
    toast.success('Opening latest APK…');
    closeDrawer();
    setAndroidModalOpen(false);
  };

  const handleCopyLink = async () => {
    if (!hasShareTarget) {
      toast.error('No link to copy. Configure version.json or Android URLs in .env.');
      return;
    }
    try {
      await navigator.clipboard.writeText(primaryShareUrl);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleNativeShare = async () => {
    if (!hasShareTarget) {
      void handleCopyLink();
      return;
    }
    if (!navigator.share) {
      void handleCopyLink();
      return;
    }
    try {
      await navigator.share({
        title: 'Guard Android app',
        text: manifest ? `Guard Android ${manifest.version}` : 'Download the Guard Android app',
        url: primaryShareUrl,
      });
      closeDrawer();
      setAndroidModalOpen(false);
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        void handleCopyLink();
      }
    }
  };

  const openAndroidModal = () => setAndroidModalOpen(true);

  const showIosIcon = IOS_DOWNLOAD_URL.length > 0;
  const showWindowsIcon = WINDOWS_DOWNLOAD_URL.length > 0;

  const latestButtonDisabled = versionLoading || !hasLatestDownload;

  return (
    <>
      <DownloadStrip>
        <DownloadLabel>Download</DownloadLabel>
        <PlatformIcons>
          <PlatformIconButton
            type="button"
            aria-label="Android app: download or share link"
            title="Android"
            $accent="#3ddc84"
            onClick={openAndroidModal}
          >
            <AndroidIconGlyph />
          </PlatformIconButton>
          {showIosIcon ? (
            <PlatformIconButton
              type="button"
              aria-label="iOS app"
              title="iOS"
              onClick={() => {
                window.open(IOS_DOWNLOAD_URL, '_blank', 'noopener,noreferrer');
                closeDrawer();
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden focusable="false">
                <path
                  fill="currentColor"
                  d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"
                />
              </svg>
            </PlatformIconButton>
          ) : null}
          {showWindowsIcon ? (
            <PlatformIconButton
              type="button"
              aria-label="Windows app"
              title="Windows"
              onClick={() => {
                window.open(WINDOWS_DOWNLOAD_URL, '_blank', 'noopener,noreferrer');
                closeDrawer();
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden focusable="false">
                <path fill="currentColor" d="M3 5.5l7-1v6.5H3V5.5zm0 7.5h7V21l-7-1v-7zm8.5-8.4L21 3v8.5h-9.5V4.6zm0 9.9H21V21l-9.5-1.3v-6.2z" />
              </svg>
            </PlatformIconButton>
          ) : null}
        </PlatformIcons>
      </DownloadStrip>

      <Modal
        isOpen={androidModalOpen}
        onClose={() => setAndroidModalOpen(false)}
        title="Android app — downloads"
        size="md"
        closeOnOverlayClick
        closeOnEsc
        footer={
          <Button type="button" variant="secondary" onClick={() => setAndroidModalOpen(false)}>
            Close
          </Button>
        }
      >
        <ModalHint as="div" style={{ marginTop: 0, fontWeight: 600, color: theme.colors.light.text.primary }}>
          Latest build from <code style={{ fontSize: '0.85em' }}>version.json</code> · Base / Drive URL from{' '}
          <code style={{ fontSize: '0.85em' }}>.env</code>
        </ModalHint>
        {versionLoading ? (
          <ModalHint>Loading release info from version.json…</ModalHint>
        ) : null}
        {versionError ? (
          <ModalWarning>
            {versionError}. You can still use the base APK link from .env if it is configured.
          </ModalWarning>
        ) : null}
        {manifest ? (
          <>
            <VersionMeta>
              Latest: v{manifest.version} (build {manifest.buildNumber})
              {manifest.forceUpdate ? ' · Update required' : ''}
            </VersionMeta>
            {manifest.releaseNotes?.trim() ? (
              <ReleaseNotesBlock>{manifest.releaseNotes.trim()}</ReleaseNotesBlock>
            ) : null}
            {manifest.minVersion ? (
              <ModalHint style={{ marginTop: 0 }}>Minimum supported version: {manifest.minVersion}</ModalHint>
            ) : null}
          </>
        ) : !versionLoading && !versionError ? (
          <ModalHint>No version.json data yet. Check the manifest URL or your connection.</ModalHint>
        ) : null}

        {!hasBaseDownloadUrl && !hasLatestDownload && !versionLoading ? (
          <ModalWarning>
            Set <strong>REACT_APP_ANDROID_VERSION_JSON_URL</strong> (optional; defaults to guards.dynamixzone.com) and/or{' '}
            <strong>REACT_APP_ANDROID_DRIVE_URL</strong> in <code style={{ fontSize: '0.85em' }}>.env</code>, then restart the dev server.
          </ModalWarning>
        ) : null}

        <ModalActions>
          <DownloadStack>
            <div>
              <Button
                type="button"
                dark
                size="sm"
                fullWidth
                loading={versionLoading}
                disabled={latestButtonDisabled}
                onClick={handleOpenLatestApk}
              >
                <FiDownload size={16} aria-hidden />
                Download Latest APK
              </Button>
              {hasLatestDownload ? (
                <LatestUrlHint title={latestDownloadUrl}>{displayUrl(latestDownloadUrl, 120)}</LatestUrlHint>
              ) : null}
            </div>

            <BaseDriveSection>
              <BaseDriveTitle>Base APK (Drive / env)</BaseDriveTitle>
              {hasBaseDownloadUrl ? (
                <BaseDriveLink
                  href={BASE_DOWNLOAD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    toast.success('Opening base APK / Drive…');
                    closeDrawer();
                    setAndroidModalOpen(false);
                  }}
                >
                  {displayUrl(BASE_DOWNLOAD_URL, 220)}
                </BaseDriveLink>
              ) : (
                <BaseDriveMissing>
                  No Drive URL in <code>.env</code>. Set <strong>REACT_APP_ANDROID_DRIVE_URL</strong> or{' '}
                  <strong>REACT_APP_ANDROID_DOWNLOAD_URL</strong>, then restart the app.
                </BaseDriveMissing>
              )}
            </BaseDriveSection>
          </DownloadStack>

          <ModalHint style={{ marginBottom: 0 }}>
            Copy / Share use the <strong>latest APK</strong> link when version.json loads; otherwise your env share / Drive URL.
          </ModalHint>

          <CopyShareRow>
            <CopyShareRowButton>
              <Button
                type="button"
                variant="secondary"
                dark
                size="sm"
                onClick={() => void handleCopyLink()}
                disabled={!hasShareTarget}
              >
                <FiLink size={16} aria-hidden />
                Copy
              </Button>
            </CopyShareRowButton>
            <CopyShareRowButton>
              <Button
                type="button"
                variant="secondary"
                dark
                size="sm"
                onClick={() => void handleNativeShare()}
                disabled={!hasShareTarget}
              >
                <FiSend size={16} aria-hidden />
                Share
              </Button>
            </CopyShareRowButton>
          </CopyShareRow>
        </ModalActions>
      </Modal>
    </>
  );
};

export default SidebarAndroidDownload;
